/**
 * LiensListPage
 * Vista global paginada de WalletLiens con filtros (status, partner).
 *
 * Backend: GET /credit-worker/backoffice/liens
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { ROUTES } from '@/constants';
import { useLiens } from '@/features/collection/hooks';
import { formatCurrencyMXN } from '@/features/collection/types';
import type { WalletLienStatus } from '@/types/lien.types';

const BREADCRUMBS = [
  { label: 'Inicio', path: ROUTES.DASHBOARD },
  { label: 'Cobranza', path: ROUTES.COLLECTION_DASHBOARD },
  { label: 'Wallet Liens', path: ROUTES.LIENS_LIST },
];

const STATUS_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'ACTIVE', label: 'ACTIVE' },
  { value: 'PAID_OFF', label: 'PAID_OFF' },
  { value: 'EXPIRED', label: 'EXPIRED' },
  { value: 'WAIVED', label: 'WAIVED' },
];

function statusVariant(s: string): 'default' | 'success' | 'warning' | 'danger' {
  if (s === 'ACTIVE') return 'warning';
  if (s === 'PAID_OFF') return 'success';
  if (s === 'EXPIRED') return 'danger';
  return 'default';
}

function formatEpoch(ts: number | null | undefined): string {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: '2-digit' });
}

export function LiensListPage() {
  const [status, setStatus] = useState<WalletLienStatus | ''>('ACTIVE');
  const [partnerId, setPartnerId] = useState<string>('');

  const { items, isLoading, isError, refetch } = useLiens({
    status: status || undefined,
    partnerId: partnerId || undefined,
    pageSize: 50,
  });

  return (
    <div>
      <PageHeader
        title="Wallet Liens"
        subtitle="Cobro latente sobre créditos DEFAULTED"
        breadcrumbs={BREADCRUMBS}
        actions={
          <Button onClick={refetch}>
            <RefreshCw size={16} /> Actualizar
          </Button>
        }
      />

      <Card title="Filtros">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[150px]">
            <Select
              label="Status"
              value={status}
              options={STATUS_OPTIONS}
              onChange={(v) => setStatus(v as WalletLienStatus | '')}
            />
          </div>
          <div className="min-w-[150px]">
            <label className="mb-1 block text-xs text-muted-foreground">Partner</label>
            <Input
              placeholder="UNE"
              value={partnerId}
              onChange={(e) => setPartnerId(e.target.value)}
            />
          </div>
        </div>
      </Card>

      <div className="mt-4">
        <Card title="Resultados" description={`${items.length} lien(s)`}>
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-8 w-full" />)}
            </div>
          ) : isError ? (
            <p className="text-sm text-danger">Error cargando liens.</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground">No hay liens con esos filtros.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b text-left text-muted-foreground">
                  <tr>
                    <th className="p-2">Lien</th>
                    <th className="p-2">Credit</th>
                    <th className="p-2">Partner</th>
                    <th className="p-2 text-right">Original</th>
                    <th className="p-2 text-right">Recuperado</th>
                    <th className="p-2 text-right">Restante</th>
                    <th className="p-2">Status</th>
                    <th className="p-2">Creado</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(l => (
                    <tr key={l.lien_id} className="border-b last:border-0 hover:bg-muted/40">
                      <td className="p-2 font-mono text-xs">{l.lien_id.slice(0, 8)}…</td>
                      <td className="p-2 font-mono text-xs">{l.credit_id.slice(0, 8)}…</td>
                      <td className="p-2">{l.partner_id}</td>
                      <td className="p-2 text-right">{formatCurrencyMXN(l.original_balance_mxn)}</td>
                      <td className="p-2 text-right">{formatCurrencyMXN(l.total_collected_mxn)}</td>
                      <td className="p-2 text-right font-medium">{formatCurrencyMXN(l.current_balance_mxn)}</td>
                      <td className="p-2"><Badge variant={statusVariant(l.status)}>{l.status}</Badge></td>
                      <td className="p-2 text-xs text-muted-foreground">{formatEpoch(l.created_at)}</td>
                      <td className="p-2">
                        <Link
                          to={ROUTES.LIEN_DETAIL.replace(':lienId', l.lien_id)}
                          className="text-primary hover:underline"
                        >
                          Detalle →
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
